'use client';

import FavoriteModal from "@/components/common/favorite_modal/favorite_modal";
import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";
import { signOut, useSession } from "next-auth/react";
import { createContext, useEffect, useState } from "react";
import { clearBackendTokenFromSecureStorage, restoreAxiosAuthFromSecureStorage, saveBackendTokenToSecureStorage } from "@/utils/mobile-secure-token";

const defaultUserData = {
    id:0,
    username:"",
    name:"",
    email:"",
    photo:"",
    notifications:[]
}

const AuthContext = createContext({
    userData: defaultUserData,
    setUserData: function(user_data) {},
    showFavouriteModal: () => {},
    tempFavouriteIds: [],
    setTempFavouriteIds: () => {},
    tempFavouriteNotes: [],
    setTempFavouriteNotes: () => {},
    showLogin: false,
    setShowLogin: () => {},
    showLoginState: "",
    setShowLoginState: () => {},
    redirectURL: "",
    setRedirectURL: () => {},
    weddingId: 0,
    weddingDate: "",
    setWeddingDate: () => {},
    wedding: {},
    loginIsProvider: false,
    setLoginIsProvider: () => {},
    provLoginRegFn: () => {},
    setProvLoginRegFn: () => {}
});


export function AuthContextProvider(props) {
    const [userData, setUserData] = useState(defaultUserData);
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [weddingId, setWeddingId] = useState(0);
    const [weddingDate, setWeddingDate] = useState("");
    const [wedding, setWedding] = useState({});
    const [tempFavouriteIds, setTempFavouriteIds] = useState([]);
    const [tempFavouriteNotes, setTempFavouriteNotes] = useState([]);
    const {data, status} = useSession();
    const [showLogin, setShowLogin] = useState(false);
    const [showLoginState, setShowLoginState] = useState("login");
    const [redirectURL, setRedirectURL] = useState("");
    const [tempNoteId, setTempNoteId] = useState(0);

    const [loginIsProvider, setLoginIsProvider] = useState(false);
    const [provLoginRegFn, setProvLoginRegFn] = useState(() => {});

    useEffect(() => {
        restoreAxiosAuthFromSecureStorage();
    }, []);

    useEffect(() => {
        if(!showLogin) setLoginIsProvider(false);
    }, [showLogin])

    const { data: weddings } = useQuery({
        queryKey: ['weddings'],
        queryFn: () => axios.get(`${process.env.NEXT_PUBLIC_BACKEND_URL}/wedding`).then(r => r.data),
        enabled: (status === "authenticated" && userData.id !== 0)
    });

    useEffect(() => {
        if(weddings && weddings.response && weddings.response.weddings && weddings.response.weddings.length > 0) {
            setWeddingId(weddings.response.weddings[0].id);
            setWeddingDate(weddings.response.weddings[0].date);
            setWedding(weddings.response.weddings[0]);
        }
    }, [weddings])

    const { data: favouriteProviders } = useQuery({
        queryKey: ['auth_favourite_providers', weddingId],
        queryFn: () => axios.get(`${process.env.NEXT_PUBLIC_BACKEND_URL}/wedding/${weddingId}/provider`, { params: { page: -1 } }).then(r => r.data?.response?.providers ?? []),
        enabled: (status === "authenticated" && userData.id !== 0 && weddingId > 0)
    });

    useEffect(() => {
        if(!favouriteProviders || favouriteProviders.length === 0) return;

        const activeFavourites = favouriteProviders.filter(p => p.status === "like" || p.status === "selected" || p.user_status === "like" || p.user_status === "selected");
        if(activeFavourites.length === 0) return;

        setTempFavouriteIds(prev => {
            const ids = new Set(prev);
            activeFavourites.forEach(p => ids.add(p.id));
            return Array.from(ids);
        });

        setTempFavouriteNotes(prev => {
            const notes = new Map(prev.map(note => [note.id, note]));
            activeFavourites.forEach(p => {
                if(!notes.has(p.id)) notes.set(p.id, {id: p.id, note: p.note || ""});
            });
            return Array.from(notes.values());
        });
    }, [favouriteProviders])
    
    const { mutate: myUser } = useMutation({
        mutationFn: () => axios.get(`${process.env.NEXT_PUBLIC_BACKEND_URL}/user`).then(r => r.data),
        onSuccess: (response) => {
            if(response.success) {
                setUserData(response.response.user);
            } else if(response.errcode === 401) {
                setUserData(defaultUserData);
                setIsLoggedIn(false);
                try { delete axios.defaults.headers.common['Authorization']; } catch (_) {}
                clearBackendTokenFromSecureStorage().catch(() => {});
                signOut();
                window.location.reload();
            }
        }
    });

    
    const showFavouriteModal = (id) => {
        if(tempNoteId === 0) setTempNoteId(id);
    }

    useEffect(() => {
        if(status === "authenticated" && data.user) {
            axios.defaults.headers.common['Authorization'] = `Bearer ${data.user.token}`;
            axios.defaults.headers.common['Content-Type'] = `application/json`;
            saveBackendTokenToSecureStorage(data.user.token);
            myUser();
            setIsLoggedIn(true);
        } else if(isLoggedIn) {
            setIsLoggedIn(true);
            try { delete axios.defaults.headers.common['Authorization']; } catch (_) {}
            clearBackendTokenFromSecureStorage();
            signOut();
        }
    }, [data, status])

    const context = {
        userData: userData,
        setUserData: (user_data) => setUserData(user_data),
        showFavouriteModal: showFavouriteModal,
        tempFavouriteIds: tempFavouriteIds,
        setTempFavouriteIds: (new_favourites) => setTempFavouriteIds(new_favourites),
        tempFavouriteNotes: tempFavouriteNotes,
        setTempFavouriteNotes: (new_favourites_notes) => setTempFavouriteNotes(new_favourites_notes),
        showLogin: showLogin,
        setShowLogin: (show_login) => setShowLogin(show_login),
        showLoginState: showLoginState,
        setShowLoginState: (login_state) => setShowLoginState(login_state),
        redirectURL: redirectURL,
        setRedirectURL: (redirect_url) => setRedirectURL(redirect_url),
        weddingId: weddingId,
        weddingDate: weddingDate,
        setWeddingDate: (date) => setWeddingDate(date),
        wedding: wedding,
        loginIsProvider: loginIsProvider,
        setLoginIsProvider: (is_provider) => setLoginIsProvider(is_provider),
        provLoginRegFn: provLoginRegFn,
        setProvLoginRegFn: (fn) => setProvLoginRegFn(fn)
    }

    return <AuthContext.Provider value={context}>
        {props.children}
        <FavoriteModal id={tempNoteId} setId={setTempNoteId} setShowLogin={setShowLogin} setShowLoginState={setShowLoginState} setRedirectURL={setRedirectURL} tempFavouriteIds={tempFavouriteIds} setTempFavouriteIds={setTempFavouriteIds} tempFavouriteNotes={tempFavouriteNotes} setTempFavouriteNotes={setTempFavouriteNotes} weddingId={weddingId} status={status} />
    </AuthContext.Provider>
}

export default AuthContext;